import { LoadingPanel } from "@/components/LoadingPanel";
import { getSupabase } from "@/lib/supabase";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type OtpType = "signup" | "invite" | "magiclink" | "recovery" | "email_change" | "email";

const ALLOWED: OtpType[] = ["signup", "invite", "magiclink", "recovery", "email_change", "email"];

function safeNext(raw: string | null): string {
  if (!raw) return "/";
  if (!raw.startsWith("/")) return "/";
  if (raw.startsWith("//")) return "/";
  return raw;
}

/**
 * Token-hash sign-in handler. Email links land here with `?token_hash=...&type=...`.
 * The verify call is a JS POST so URL prefetchers (Gmail safe-browsing,
 * Outlook SafeLinks, etc.) can't consume the single-use token.
 */
export function AuthConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const tokenHash = params.get("token_hash");
    const type = params.get("type") as OtpType | null;
    const next = safeNext(params.get("next"));
    if (!tokenHash || !type || !ALLOWED.includes(type)) {
      setError("This link is missing or malformed. Request a new one from the login page.");
      return;
    }
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      const { error: vErr } = await sb.auth.verifyOtp({ token_hash: tokenHash, type });
      if (cancelled) return;
      if (vErr) {
        setError(vErr.message ?? "This link is invalid or has expired.");
        return;
      }
      navigate(next, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center space-y-4">
          <h1 className="text-xl font-semibold text-ink">Sign-in link didn't work</h1>
          <p className="text-sm text-ink-dim">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="h-11 inline-flex items-center justify-center px-5 rounded-xl bg-accent text-accent-on font-medium"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-dvh flex items-center justify-center">
      <LoadingPanel label="Signing you in…" />
    </div>
  );
}
